import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useServerData } from '@xingternal/hops';
import { Headline, BodyCopy } from '@xingternal/typography';
import { Link } from 'react-router-dom';

import { MetaData } from '@/components/MetaData';
import { PageError } from '@/components/PageError';
import { useCompaniesIndustries } from '@/hooks/useCompaniesIndustries';
import { useTrackPage } from '@/hooks/useTrackPage';
import { getIndustryRoute, getIndustrySearchPageRoute } from '@/routes/routes';
import { getIndustryName } from '@/utils/url';
import { formatAmount } from '@/utils/formatAmount';
import { pagesTrackingName } from '@/utils/tracking';
import { SkeletonLoggedOutIndustriesPage } from '@/components/Skeleton';

import styles from './Industries.css';

const INDUSTRIES_LIMIT = 100;
type IndustriesContent = {
  link: string;
  className: string;
  total: string;
  localizationValue: string;
  id: string;
};
const IndustryContent = ({
  localizationValue,
  total,
}: {
  localizationValue: string;
  total: string;
}) => (
  <>
    <BodyCopy fontWeight="bold" size="small" noMargin>
      {localizationValue}
    </BodyCopy>
    <BodyCopy size="small" noMargin>
      <FormattedMessage id={'CP_TOTAL_COMPANIES'} values={{ total }} />
    </BodyCopy>
  </>
);

const LoggedInIndustryContent = ({
  link,
  localizationValue,
  total,
  id,
  className,
}: IndustriesContent) => (
  <a
    key={id}
    className={className}
    href={link}
    data-testid={`loggedin-industry-${id}`}
  >
    <IndustryContent localizationValue={localizationValue} total={total} />
  </a>
);

const LoggedOutIndustryContent = ({
  link,
  localizationValue,
  total,
  id,
  className,
}: IndustriesContent) => (
  <Link
    key={id}
    className={className}
    to={link}
    data-testid={`loggedout-industry-${id}`}
  >
    <IndustryContent localizationValue={localizationValue} total={total} />
  </Link>
);

const Industries: React.FC = () => {
  const intl = useIntl();
  const { authenticated } = useServerData();
  const { data, loading, error } = useCompaniesIndustries({
    limit: INDUSTRIES_LIMIT,
  });

  useTrackPage({ pageName: pagesTrackingName.industries });

  if (loading) return <SkeletonLoggedOutIndustriesPage />;
  if (error) return <PageError />;

  return (
    <>
      <MetaData
        index
        title={intl.formatMessage({ id: 'CP_100_META_TITLE_INDUSTRIES' })}
        description={intl.formatMessage({
          id: 'CP_INDUSTRIES_DESCRIPTION',
        })}
      />
      <Headline size="xlarge" as="h1" className={styles.headline}>
        <FormattedMessage id="CP_100_META_TITLE_INDUSTRIES" />
      </Headline>
      <BodyCopy size="medium">
        <FormattedMessage id="CP_100_DIRECTORY_INTRO" />
      </BodyCopy>
      <BodyCopy fontWeight="bold" size="medium">
        <FormattedMessage id="CP_INDUSTRIES_DESCRIPTION" />
      </BodyCopy>
      <div className={styles.container}>
        {data?.companiesIndustries?.collection.map((item) => {
          const id = item?.industry?.id as string;
          const localizationValue = item?.industry?.localizationValue as string;
          const total = item?.total as number;

          const link = authenticated
            ? getIndustrySearchPageRoute({ id })
            : getIndustryRoute({ id, name: getIndustryName({ intl, id }) });

          const IndustryComponent = authenticated
            ? LoggedInIndustryContent
            : LoggedOutIndustryContent;

          return (
            <IndustryComponent
              key={id}
              id={id}
              className={styles.industry}
              link={link}
              total={formatAmount({ value: total, intl })}
              localizationValue={localizationValue}
            />
          );
        })}
      </div>
    </>
  );
};

export { Industries, INDUSTRIES_LIMIT };
